import { FC, useCallback, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Fade,
  Flex,
  Icon,
  MenuItem,
  SlideFade,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { captureException } from '@sentry/react';
import { head, isNil } from 'ramda';
import { FileWithPath, useDropzone } from 'react-dropzone';
import {
  BiCheckCircle,
  BiCloudUpload,
  BiErrorCircle,
  BiHelpCircle,
  BiLink,
} from 'react-icons/bi';
import {
  calculateChunkSize,
  hashFile,
} from '../../../../lib/hash-file/hash-file';

type Props = {
  hash?: string | null;
};

export const FileValidationAction: FC<Props> = ({ hash }) => {
  const cancelRef = useRef<HTMLElement>(null);
  const [isValid, setIsValid] = useState<boolean | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleClose = () => {
    setIsValid(undefined);
    onClose();
  };

  const handleValidateFile = useCallback(
    (files: FileWithPath[]) => {
      (async () => {
        setLoading(true);

        try {
          const file = head(files);

          if (!file) {
            setIsValid(false);
            return;
          }

          const localFileHash = await hashFile({
            file,
            chunkSize: calculateChunkSize(5),
          });

          if (localFileHash !== hash) {
            setIsValid(false);
            return;
          }
          setIsValid(true);
        } catch (e) {
          captureException(e);
        } finally {
          setLoading(false);
        }
      })();
    },
    [hash]
  );

  const { getInputProps, open } = useDropzone({
    onDrop: handleValidateFile,
    maxFiles: 1,
    noClick: true,
  });

  const handleOpenUpload = () => {
    setIsValid(undefined);
    open();
  };

  const renderFileValidationContent = () => {
    if (loading) {
      return (
        <>
          <AlertDialogHeader
            py="6"
            bg="yellow.400"
            justifyContent="center"
            textAlign="center"
          >
            <Spinner color="yellow.500" size="xl" thickness="3px" />
            <AlertDialogCloseButton
              size="sm"
              color="yellow.200"
              alignSelf="flex-end"
            />
          </AlertDialogHeader>

          <AlertDialogBody textAlign="center" py="6">
            <Text fontSize="2xl" fontWeight="semibold">
              Validating File
            </Text>
          </AlertDialogBody>
        </>
      );
    }

    if (isNil(isValid)) {
      return (
        <Fade in data-testid="dlt-select-file">
          <AlertDialogHeader textAlign="center">Match File</AlertDialogHeader>
          <AlertDialogCloseButton size="sm" alignSelf="flex-end" />
          <AlertDialogBody textAlign="center" pb={4}>
            <VStack spacing={8}>
              <Text>
                Select a file to validate it against the latest version in the
                Distributed Ledger.
              </Text>
              <Button
                size="md"
                colorScheme="green"
                onClick={handleOpenUpload}
                leftIcon={<Icon as={BiCloudUpload} fontSize="20" />}
              >
                Select file
              </Button>
            </VStack>
          </AlertDialogBody>
        </Fade>
      );
    }

    if (!isValid) {
      return (
        <Fade in data-testid="dlt-invalid">
          <AlertDialogHeader
            p="0"
            bg="red.400"
            justifyContent="center"
            textAlign="center"
          >
            <SlideFade in offsetY="40">
              <Icon as={BiErrorCircle} fontSize={64} color="white" />
            </SlideFade>
            <AlertDialogCloseButton
              size="sm"
              color="red.100"
              alignSelf="flex-end"
            />
          </AlertDialogHeader>

          <AlertDialogBody textAlign="center" py="6">
            <VStack spacing="6">
              <VStack>
                <Text fontSize="2xl" fontWeight="semibold">
                  File Mismatch
                </Text>
                <Text>
                  The file you selected did not match the file in the
                  Distributed Ledger.
                </Text>
              </VStack>
              <Button
                size="md"
                onClick={() => { open(); }}
                colorScheme="blue"
                leftIcon={<Icon as={BiCloudUpload} fontSize="20" />}
              >
                Select another file
              </Button>
            </VStack>
          </AlertDialogBody>
        </Fade>
      );
    }

    return (
      <Fade in data-testid="dlt-valid">
        <AlertDialogHeader
          p="0"
          bg="green.400"
          justifyContent="center"
          textAlign="center"
        >
          <SlideFade in offsetY="40">
            <Icon as={BiCheckCircle} fontSize={64} color="white" />
          </SlideFade>
          <AlertDialogCloseButton
            size="sm"
            color="green.100"
            alignSelf="flex-end"
          />
        </AlertDialogHeader>
        <AlertDialogBody textAlign="center" py="6">
          <VStack>
            <Text fontSize="2xl" fontWeight="semibold">
              File Valid
            </Text>
            <Text>
              The file you selected matches the file in the Distributed Ledger.
            </Text>
          </VStack>
        </AlertDialogBody>
      </Fade>
    );
  };

  return (
    <>
      <MenuItem
        onClick={onOpen}
        isDisabled={!hash}
        data-testid="validate-file-action"
        icon={
          <span>
            <Icon as={BiLink} color="blue.500" fontSize={16} />
          </span>
        }
      >
        <Flex
          as="span"
          alignItems="center"
          pointerEvents="all"
          justifyContent="space-between"
        >
          Match File
          <Tooltip
            placement="right"
            label="Select a local file to compare it against the version in the Distributed Ledger to see if they are the same."
          >
            <span>
              <Icon as={BiHelpCircle} color="gray.500" fontSize={16} />
            </span>
          </Tooltip>
        </Flex>
      </MenuItem>
      <input {...getInputProps()} data-testid="upload-input" />

      <AlertDialog
        isCentered
        isOpen={isOpen}
        motionPreset="scale"
        onClose={handleClose}
        closeOnOverlayClick={false}
        leastDestructiveRef={cancelRef}
      >
        <AlertDialogOverlay />
        <AlertDialogContent>{renderFileValidationContent()}</AlertDialogContent>
      </AlertDialog>
    </>
  );
};
